/// <reference types="vite/client" />
import { ToastProvider } from "@credit-acceptance/uikit";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { defaultShouldDehydrateQuery, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { type ComponentProps, type PropsWithChildren, useState } from "react";

import { createQueryClient } from "../api/queryClient";
import { isCallScriptQuestionsQueryKey } from "../features/review/sections/Signers/api/callScriptKeys";
import { isCallScriptResidenceQuestionsQueryKey } from "../features/review/sections/Signers/api/callScriptResidenceKeys";
import { CALL_SCRIPT_QUESTIONS_STALE_TIME } from "../features/review/sections/Signers/api/useCallScriptQuestions";

// Only the Call Script verification-question catalogs (employment and
// residence) are persisted — they're global reference data (no
// approvalId/applicantId), unlike every other cached query in this app,
// which is applicant/deal-specific and must never be written to localStorage.
const callScriptQuestionsPersister = createAsyncStoragePersister({
  storage: window.localStorage,
  key: "dsc-workbench.callScriptQuestions",
});

const persistOptions = {
  persister: callScriptQuestionsPersister,
  maxAge: CALL_SCRIPT_QUESTIONS_STALE_TIME,
  dehydrateOptions: {
    shouldDehydrateQuery: (query: Parameters<typeof defaultShouldDehydrateQuery>[0]) =>
      defaultShouldDehydrateQuery(query) &&
      (isCallScriptQuestionsQueryKey(query.queryKey) || isCallScriptResidenceQuestionsQueryKey(query.queryKey)),
  },
};

// Cross-cutting providers compose here as they land:
//   <AuthProvider>
//     <FeatureFlagProvider>
//       <ThemeProvider>
//         <ToastProvider>{children}</ToastProvider>
//       </ThemeProvider>
//     </FeatureFlagProvider>
//   </AuthProvider>
// Wired once in App.tsx. Features do not wrap themselves.
//
// `QueryClientProviderChildren` borrows the children type that
// `<QueryClientProvider>` itself declares. TanStack Query's bundled
// `.d.ts` uses `import * as React_2 from "react"` and references
// `React_2.ReactNode`; when tsc resolves that import through the
// pnpm store it can land on a different `@types/react` copy than
// the one this file's `react` import resolves to. Aliasing here
// keeps the cast aligned with whatever copy TanStack itself sees.
type QueryClientProviderChildren = ComponentProps<typeof QueryClientProvider>["children"];

function AppProviders({ children }: PropsWithChildren) {
  const [queryClient] = useState(createQueryClient);

  return (
    <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
      <ToastProvider>{children as QueryClientProviderChildren}</ToastProvider>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </PersistQueryClientProvider>
  );
}

export default AppProviders;
